import { SdkContext } from '../internal'
import { listItems } from '@/lib/utils'
import type {
  InspectionRound,
  InspectionSector,
  InspectionSpot,
  ResourceHistory,
  RoundItem,
  RoundItemInput,
  RoundPage,
  SupportClosureInput,
  SupportClosurePreview,
  SupportCreateInput,
  SupportListParams,
  SupportPage,
  SupportPublicConfig,
  SupportReport,
  SupportSummary,
  SupportTicket,
  SupportTicketDetail,
  SupportUpdateInput
} from '@/types/support'

/**
 * F-82: Support tickets. Paths are written by hand (F-03). The community comes from the
 * `X-CommunityId` header; the visibility of each ticket (own, whole queue, internal notes) and the
 * `can*` flags are decided by the server.
 */
export class SupportResource {
  constructor(private ctx: SdkContext) {}

  async config(spaceId: string): Promise<SupportPublicConfig> {
    return this.ctx.call({ httpMethod: 'GET', path: '/support/config' }, { query: { spaceId } })
  }

  /** Team only. Without `spaceId` it adds up every Support space. */
  async summary(spaceId?: string): Promise<SupportSummary> {
    return this.ctx.call({ httpMethod: 'GET', path: '/support/summary' }, { query: { spaceId } })
  }

  /** The duplicate warning: only how many open tickets the resource has, never their content. */
  async openCount(resourceId: string): Promise<{ resourceId: string; count: number }> {
    return this.ctx.call({ httpMethod: 'GET', path: '/support/resources/{resourceId}/open-count' }, { resourceId })
  }

  async list(params: SupportListParams = {}): Promise<SupportPage> {
    const { status, priority, category, unassigned, mine, overdue, dueToday, q, ...rest } = params
    const raw = await this.ctx.raw(
      { httpMethod: 'GET', path: '/support/tickets' },
      {
        query: {
          ...rest,
          status: status?.length ? status.join(',') : undefined,
          priority: priority?.length ? priority.join(',') : undefined,
          category: category?.length ? category.join(',') : undefined,
          unassigned: unassigned ? 'true' : undefined,
          mine: mine ? 'true' : undefined,
          overdue: overdue ? 'true' : undefined,
          dueToday: dueToday ? 'true' : undefined,
          q: q || undefined
        }
      }
    ) as { items?: SupportTicket[]; pagination?: SupportPage['pagination'] } | SupportTicket[] | null
    return { items: listItems<SupportTicket>(raw), pagination: (!Array.isArray(raw) && raw?.pagination) || null }
  }

  /** `alreadyCreated` is true when the same `clientRequestId` had opened the ticket before. */
  async create(body: SupportCreateInput): Promise<{ ticket: SupportTicketDetail; alreadyCreated: boolean }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/support/tickets', hasBody: true }, { body })
  }

  async get(ticketId: string): Promise<SupportTicketDetail> {
    return this.ctx.call({ httpMethod: 'GET', path: '/support/tickets/{ticketId}' }, { ticketId })
  }

  async update(ticketId: string, body: SupportUpdateInput): Promise<SupportTicketDetail> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/support/tickets/{ticketId}', hasBody: true }, { ticketId, body })
  }

  /** Only who answers the ticket's category (the team picker of "assign"). */
  async assignees(ticketId: string): Promise<Array<{ id: string; displayName: string | null; avatarUrl: string | null }>> {
    const result = await this.ctx.call<Array<{ id: string; displayName: string | null; avatarUrl: string | null }>>({ httpMethod: 'GET', path: '/support/tickets/{ticketId}/assignees' }, { ticketId })
    return Array.isArray(result) ? result : []
  }

  async cancel(ticketId: string): Promise<SupportTicketDetail> {
    return this.ctx.call({ httpMethod: 'POST', path: '/support/tickets/{ticketId}/cancel' }, { ticketId })
  }

  async reopen(ticketId: string): Promise<SupportTicketDetail> {
    return this.ctx.call({ httpMethod: 'POST', path: '/support/tickets/{ticketId}/reopen' }, { ticketId })
  }

  /** `internal` is the team's private note; the server refuses it from a resident. */
  async comment(ticketId: string, body: { body: string; internal?: boolean; mediaIds?: string[] }): Promise<{ ticket: SupportTicketDetail; commentId: string }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/support/tickets/{ticketId}/comments', hasBody: true }, { ticketId, body })
  }

  async removeComment(ticketId: string, commentId: string): Promise<void> {
    await this.ctx.call({ httpMethod: 'DELETE', path: '/support/tickets/{ticketId}/comments/{commentId}' }, { ticketId, commentId })
  }

  async addPhoto(ticketId: string, mediaId: string): Promise<void> {
    await this.ctx.call({ httpMethod: 'POST', path: '/support/tickets/{ticketId}/photos', hasBody: true }, { ticketId, body: { mediaId } })
  }

  async removePhoto(ticketId: string, photoId: string): Promise<void> {
    await this.ctx.call({ httpMethod: 'DELETE', path: '/support/tickets/{ticketId}/photos/{photoId}' }, { ticketId, photoId })
  }

  /** Owner and admin: what closing the room would do (bookings in the way, credits that would go back). Writes nothing. */
  async previewClosure(ticketId: string, body: SupportClosureInput): Promise<SupportClosurePreview> {
    return this.ctx.call({ httpMethod: 'POST', path: '/support/tickets/{ticketId}/close-resource/preview', hasBody: true }, { ticketId, body })
  }

  /** `confirm: true` is mandatory; with bookings in the way and no `cancelBookings` the API answers 409 with the list. */
  async closeResource(ticketId: string, body: SupportClosureInput & { cancelBookings?: boolean }): Promise<{ ticket: SupportTicketDetail; closure: { canceledBookings: number } }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/support/tickets/{ticketId}/close-resource', hasBody: true }, { ticketId, body: { ...body, confirm: true } })
  }

  async reopenResource(ticketId: string): Promise<{ ticket: SupportTicketDetail }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/support/tickets/{ticketId}/reopen-resource' }, { ticketId })
  }

  /** Team: counts and averages for a local-date period (`AAAA-MM-DD`, both included). */
  async report(params: { spaceId?: string; from?: string; to?: string }): Promise<SupportReport> {
    return this.ctx.call({ httpMethod: 'GET', path: '/support/report' }, { query: { ...params, format: 'json' } })
  }

  async reportCsv(params: { spaceId?: string; from?: string; to?: string }): Promise<string> {
    const result = await this.ctx.raw<unknown>({ httpMethod: 'GET', path: '/support/report' }, { query: { ...params, format: 'csv' } })
    return typeof result === 'string' ? result : String(result ?? '')
  }

  /* F-95: inspection rounds. Owner and admin manage sectors and spots; owner, admin and moderator walk the
     rounds; finance only reads. The `can*` flags of a round come from the server. */

  async sectors(spaceId: string): Promise<InspectionSector[]> {
    const result = await this.ctx.call<InspectionSector[]>({ httpMethod: 'GET', path: '/support/sectors' }, { query: { spaceId } })
    return Array.isArray(result) ? result : []
  }

  async createSector(body: { spaceId: string; name: string; position?: number }): Promise<InspectionSector> {
    return this.ctx.call({ httpMethod: 'POST', path: '/support/sectors', hasBody: true }, { body })
  }

  async updateSector(sectorId: string, body: { name?: string; position?: number }): Promise<InspectionSector> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/support/sectors/{sectorId}', hasBody: true }, { sectorId, body })
  }

  async deleteSector(sectorId: string): Promise<void> {
    await this.ctx.call({ httpMethod: 'DELETE', path: '/support/sectors/{sectorId}' }, { sectorId })
  }

  /** A spot is a Bookings resource (`resourceId`) or a free place (`label`). */
  async createSpot(sectorId: string, body: { resourceId?: string | null; label?: string | null; position?: number }): Promise<InspectionSpot> {
    return this.ctx.call({ httpMethod: 'POST', path: '/support/sectors/{sectorId}/spots', hasBody: true }, { sectorId, body })
  }

  async updateSpot(spotId: string, body: { resourceId?: string | null; label?: string | null; position?: number }): Promise<InspectionSpot> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/support/spots/{spotId}', hasBody: true }, { spotId, body })
  }

  async deleteSpot(spotId: string): Promise<void> {
    await this.ctx.call({ httpMethod: 'DELETE', path: '/support/spots/{spotId}' }, { spotId })
  }

  async rounds(params: { spaceId?: string; sectorId?: string; status?: 'IN_PROGRESS' | 'COMPLETED'; page?: number; limit?: number } = {}): Promise<RoundPage> {
    const raw = await this.ctx.raw({ httpMethod: 'GET', path: '/support/rounds' }, { query: params }) as { items?: InspectionRound[]; pagination?: RoundPage['pagination'] } | InspectionRound[] | null
    return { items: listItems<InspectionRound>(raw), pagination: (!Array.isArray(raw) && raw?.pagination) || null }
  }

  /** Starting on a sector with a round in progress returns that round (`alreadyStarted: true`). */
  async startRound(sectorId: string): Promise<{ round: InspectionRound; alreadyStarted: boolean }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/support/rounds', hasBody: true }, { body: { sectorId } })
  }

  async getRound(roundId: string): Promise<InspectionRound> {
    return this.ctx.call({ httpMethod: 'GET', path: '/support/rounds/{roundId}' }, { roundId })
  }

  /** A PROBLEM opens the maintenance ticket at once; a spot that already opened one is locked (OK is a 409). */
  async markRoundItem(roundId: string, body: RoundItemInput): Promise<{ item: RoundItem; ticket: SupportTicketDetail | null }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/support/rounds/{roundId}/items', hasBody: true }, { roundId, body })
  }

  /** What nobody checked becomes PENDING. */
  async completeRound(roundId: string): Promise<InspectionRound> {
    return this.ctx.call({ httpMethod: 'POST', path: '/support/rounds/{roundId}/complete' }, { roundId })
  }

  /** The team only: the rounds that checked a resource and the tickets that point at it. */
  async resourceHistory(resourceId: string): Promise<ResourceHistory> {
    return this.ctx.call({ httpMethod: 'GET', path: '/support/resources/{resourceId}/history' }, { resourceId })
  }
}
